import { computed, ref } from 'vue';

// notification_type (backend, Notification enum) → métrica da política de SLA.
// Mesma tríade do SLAPopoverCard / SLA.EVENTS.* no i18n.
export const SLA_MISSED_METRIC = {
  sla_missed_first_response: 'frt',
  sla_missed_next_response: 'nrt',
  sla_missed_resolution: 'rt',
};

// Teto de toasts individuais abertos ao mesmo tempo. Queda massiva estoura
// centenas de SLAs no mesmo tique do cron (2 min) e admin recebe TODOS os da
// conta: um toast por SLA congelava a aba (o vue-sonner 2.0.9 renderiza todos
// e mede altura em O(n²)) e a pilha cobria a caixa de resposta. Passou do
// teto, o excedente vira UM toast agregado com a contagem.
export const MAX_INDIVIDUAL_TOASTS = 3;

// Quantos toasts de SLA podem estar na tela juntos: os individuais + o
// agregado. O Toaster do Dashboard.vue usa isto no `visible-toasts` — com o
// default do sonner (3) o individual mais antigo ficava INVISÍVEL atrás do
// agregado, aberto e fora da conta ao mesmo tempo.
export const SLA_TOASTS_ON_SCREEN = MAX_INDIVIDUAL_TOASTS + 1;

// Rotas que SÃO a lista de notificações: o sino (notifications_index) e a
// caixa de entrada da sidebar (inbox_view). Abrir uma delas esvazia o
// excedente — quem abriu está vendo a lista inteira.
export const NOTIFICATIONS_VIEW_ROUTES = ['notifications_index', 'inbox_view'];

export const entryKey = notification =>
  `${notification.primary_actor_id}:${notification.notification_type}`;

// O toast é SÓ do responsável pela conversa. A notificação do backend vai pra
// mais gente (admins, participantes, fila do time) e alimenta sino/push/e-mail
// — isso NÃO muda aqui; só a tela fica restrita. Sem responsável = sem toast.
// FRT com a 1ª resposta já dada: o cron (2 min) descobre o estouro depois do
// prazo, e se o agente respondeu nesse meio-tempo o toast chegava DEPOIS da
// resposta, cobrando o que já foi feito (dev, conta 1, conv 230: resposta
// 16:59:42, toast 17:00:06). O evento segue verdadeiro no relatório e no sino.
// Lê o retrato da conversa que veio NA notificação (criada junto do evento).
// Tempo de resolução (RT) NÃO abre toast (decisão 2026-09-24): o pop-up puxa
// uma ação imediata, e resolver muitas vezes não depende do agente (cliente
// testando, visita técnica, outro setor) — e o estouro de RT é definitivo,
// nada que ele faça depois desfaz. Fica no sino, no relatório e no card.
export const shouldShowSlaMissedToast = (notification, currentUserId) => {
  const conversation = notification.primary_actor || {};
  if (!currentUserId) return false;
  if (conversation.meta?.assignee?.id !== currentUserId) return false;

  const metric = SLA_MISSED_METRIC[notification.notification_type];
  if (metric === 'rt') return false;
  return !(metric === 'frt' && conversation.first_reply_created_at);
};

// display_id da conversa aberta na rota, ou null. Toda rota de conversa usa o
// param `conversation_id`; a caixa de entrada (inbox_view_conversation) usa
// `:type/:id`. É o `primary_actor.id` da notificação (display_id), não o
// `primary_actor_id` (id do banco).
export const conversationDisplayIdFromRoute = route => {
  const { name, params = {} } = route || {};
  if (params.conversation_id) return Number(params.conversation_id);
  if (name === 'inbox_view_conversation' && params.type === 'conversation') {
    return Number(params.id);
  }
  return null;
};

// Fila dos alertas "SLA estourado" em aberto, em ordem de chegada. Uma entrada
// por conversa+métrica ENQUANTO estiver aberta: o NRT pode estourar de novo na
// mesma conversa (o backend grava um SlaEvent por mensagem do cliente) e,
// depois de fechado, tem que reaparecer — por isso o dedup é só contra o que
// está aberto, sem histórico. As `maxIndividual` primeiras viram toast
// próprio; o resto é o excedente, contado no agregado. Fechou um individual,
// o mais antigo do excedente sobe (FIFO: o estouro mais velho é o mais urgente).
export function useSlaMissedQueue({
  maxIndividual = MAX_INDIVIDUAL_TOASTS,
} = {}) {
  const entries = ref([]);
  let sequence = 0;

  const individual = computed(() => entries.value.slice(0, maxIndividual));
  const overflowCount = computed(() =>
    Math.max(entries.value.length - maxIndividual, 0)
  );

  // Devolve a entrada nova ({ id, key, metric, notification, notificationIds })
  // quando a notificação entrou, ou null quando já havia alerta aberto pra essa
  // chave (o chamador toca o som só no primeiro caso). A repetida não abre
  // nada, mas o id dela fica na entrada: ler ou apagar QUALQUER uma das duas
  // no sino fecha o alerta. Tipo desconhecido é bug de fio: grita.
  const add = notification => {
    const metric = SLA_MISSED_METRIC[notification.notification_type];
    if (!metric) {
      throw new Error(
        `useSlaMissedQueue: notification_type inesperado "${notification.notification_type}"`
      );
    }
    const key = entryKey(notification);
    if (entries.value.some(entry => entry.key === key)) {
      entries.value = entries.value.map(entry =>
        entry.key === key
          ? {
              ...entry,
              notificationIds: [...entry.notificationIds, notification.id],
            }
          : entry
      );
      return null;
    }

    sequence += 1;
    const entry = {
      // Id do toast no sonner — único por abertura, não a chave: o sonner só
      // tira o toast da tela ~200 ms depois do dismiss, e reabrir a MESMA
      // chave nesse meio-tempo atualizaria o toast que está saindo.
      id: `sla-missed-${sequence}`,
      key,
      metric,
      notification,
      notificationIds: [notification.id],
    };
    entries.value = [...entries.value, entry];
    return entry;
  };

  const removeWhere = predicate => {
    const kept = entries.value.filter(entry => !predicate(entry));
    const removed = entries.value.length - kept.length;
    if (removed) entries.value = kept;
    return removed;
  };

  // Toast fechado (X, arrastar, "Abrir conversa").
  const remove = id => removeWhere(entry => entry.id === id);
  // Aquela notificação foi lida/adiada: fecha a conversa+métrica dela.
  const removeByKey = key => removeWhere(entry => entry.key === key);
  // Notificação apagada (payload do cable só traz o id).
  const removeByNotificationId = notificationId =>
    removeWhere(entry => entry.notificationIds.includes(notificationId));
  // Todas as notificações da conversa lidas (id do banco).
  const removeByConversation = primaryActorId =>
    removeWhere(
      entry => entry.notification.primary_actor_id === primaryActorId
    );
  // Conversa aberta na tela (display_id, vindo da rota).
  const removeByConversationDisplayId = displayId =>
    removeWhere(entry => entry.notification.primary_actor?.id === displayId);
  const removeAll = () => removeWhere(() => true);

  // Descarta o excedente sem mexer nos individuais: o agregado foi fechado ou
  // a lista de notificações foi aberta. Os descartados seguem no sino.
  const clearOverflow = () => {
    if (entries.value.length <= maxIndividual) return 0;
    const removed = entries.value.length - maxIndividual;
    entries.value = entries.value.slice(0, maxIndividual);
    return removed;
  };

  return {
    entries,
    individual,
    overflowCount,
    add,
    remove,
    removeByKey,
    removeByNotificationId,
    removeByConversation,
    removeByConversationDisplayId,
    removeAll,
    clearOverflow,
  };
}
